import React, { useEffect } from 'react';
import ChartCard from './ChartCard';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAppDispatch, useAppSelector } from '../../store/hooks/reduxHooks';
import { fetchMerchantKycStats } from '../../features/dashboard/dashboardSlice';

// Custom tooltip
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const colors = { Completed: "#10B981", Pending: "#F59E0B" };
    return (
      <div className="bg-white p-3 border border-gray-200 shadow-lg rounded-md">
        <p className="font-gilroy-semibold text-gray-700 mb-2">Merchant KYC</p>
        <div className="space-y-1.5">
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: colors[entry.name as keyof typeof colors] }} />
                <span className="text-sm">{entry.name}</span>
              </div>
              <span className="font-gilroy-semibold text-sm ml-4">{entry.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

const MerchantKycStatus: React.FC = () => {
  const dispatch = useAppDispatch();
  const { merchantKycStats, loadingKycStats, hasFetchedKycStats } = useAppSelector(state => state.dashboard);

  useEffect(() => {
    if (!hasFetchedKycStats && !loadingKycStats) {
      dispatch(fetchMerchantKycStats());
    }
  }, [dispatch, hasFetchedKycStats, loadingKycStats]);

  const stats = merchantKycStats;
  const isLoading = loadingKycStats;

  const chartData = stats ? [{
    name: 'KYC Status',
    Completed: stats.kycCompleted,
    Pending: stats.kycPending
  }] : [];

  const completedPercentage = stats ? Math.round((stats.kycCompleted / stats.totalMerchants) * 100) : 0;
  const pendingPercentage = stats ? Math.round((stats.kycPending / stats.totalMerchants) * 100) : 0;

  return (
    <ChartCard title="Merchant KYC Status" subtitle="Overview of merchant verification status" className="h-full">
      {isLoading ? (
        <div className="h-56 flex items-center justify-center">
          <div className="animate-pulse text-gray-400">Loading KYC status data...</div>
        </div>
      ) : stats ? (
        <>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }} barSize={30}>
                <defs>
                  <linearGradient id="completedKycGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#10B981" />
                    <stop offset="100%" stopColor="#059669" />
                  </linearGradient>
                  <linearGradient id="pendingKycGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#F59E0B" />
                    <stop offset="100%" stopColor="#D97706" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" horizontal vertical={false} stroke="#f0f0f0" />
                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
                <YAxis dataKey="name" type="category" hide axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(236, 236, 236, 0.2)' }} />
                <Legend wrapperStyle={{ paddingTop: '10px', fontSize: '12px' }} iconType="circle" iconSize={8} />
                <Bar dataKey="Completed" fill="url(#completedKycGradient)" stackId="a" animationDuration={1500} radius={[4, 0, 0, 4]} />
                <Bar dataKey="Pending" fill="url(#pendingKycGradient)" stackId="a" animationDuration={1500} animationBegin={300} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Progress bars */}
          <div className="space-y-5 mt-4">
            {[
              { label: 'Completed', value: stats.kycCompleted, color: 'green', percentage: completedPercentage },
              { label: 'Pending', value: stats.kycPending, color: 'yellow', percentage: pendingPercentage }
            ].map(item => (
              <div key={item.label}>
                <div className="flex justify-between mb-1">
                  <div className="flex items-center">
                    <div className={`w-3 h-3 bg-${item.color}-500 rounded-full mr-2`}></div>
                    <span className="text-sm font-gilroy-semibold">{item.label}</span>
                  </div>
                  <span className="text-sm">{item.value.toLocaleString()} ({item.percentage}%)</span>
                </div>
                <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full bg-gradient-to-r from-${item.color}-500 to-${item.color}-600 rounded-full shadow-inner`} style={{ width: `${item.percentage}%` }}></div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-white">
                  <span className="font-gilroy-bold">{stats.totalMerchants.toLocaleString()}</span>
                </div>
                <div className="ml-3">
                  <p className="text-xs text-gray-500">Total Merchants</p>
                  <p className="text-sm font-gilroy-semibold text-gray-700">All Registrations</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">KYC Completion Rate</p>
                <p className="text-sm font-gilroy-semibold text-green-500">{completedPercentage}%</p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="h-56 flex items-center justify-center">
          <div className="text-gray-500">No KYC status data available.</div>
        </div>
      )}
    </ChartCard>
  );
};

export default MerchantKycStatus;
